//@ts-nocheck
/* eslint-disable no-unused-vars */
import { Store, AnyAction } from '@reduxjs/toolkit';
import moment from 'moment';

import {
    DATAFEED_SYMBOL_TYPE,
    DEPTH,
    HEADER_SETTINGS_DEFAULTS,
    KLINE_MAX_LIMIT,
    KLINE_PRICE_ACCURACY,
} from 'src/common/constants';
import {
    digits,
    extractSymbolName,
    getHoursFromResolution,
    getResolution,
    isEmptyObj,
    isValidRegex,
} from 'src/common/helpers';
import {
    Bar,
    ErrorCallback,
    HistoryCallback,
    HistoryMetadata,
    OnReadyCallback,
    PeriodParams,
    SearchSymbolsCallback,
    LibrarySymbolInfo,
    ResolutionString,
    SearchSymbolResultItem,
    ServerTimeCallback,
} from 'src/lib/charting_library';
import {
    getServerTime,
    indexKlineHistory,
    klineHistory,
} from 'src/pages/_redux/global/globalsCrud';
import { clearKlineData } from 'src/pages/_redux/socket/socketActions';
import { RootState } from 'src/setup';
import { FuturesSymbolObj, FutureSymbol, TradeSocketResponseData } from 'src/types';

type Subscription = {
    [key: string]: {
        callback: Function;
        symbolInfo: LibrarySymbolInfo;
        lastBarTime?: number;
        subscription?: Function;
        resolution?: string;
    };
};

type HistoryCallbacks = {
    [key: string]: (bars: Bar[], meta: HistoryMetadata) => void;
};

class DataFeed {
    private _store: Store<RootState, AnyAction>;
    private _futureSymbols: FuturesSymbolObj;
    private _klineHistoryUpdated: boolean;
    private _indexKlineHistoryUpdated: boolean;
    private _currentSymbol: FutureSymbol;
    private configurationData = {
        supported_resolutions: [
            '1',
            '5',
            '15',
            '30',
            '60',
            '120',
            '240',
            '360',
            '720',
            '1440',
            'D',
            '1w',
            '1M',
        ] as ResolutionString[],
        exchanges: [],
        symbols_types: [],
        supports_time: true,
        supports_marks: false,
        supports_timescale_marks: true,
    };
    private _subscriptions: Subscription = {};
    private _historyCallbacks: HistoryCallbacks = {};
    // private _onResetCacheNeededCallback: Function = () => {};
    private _resolution: string;

    constructor(store: Store<RootState, AnyAction>) {
        this._store = store;
        this._futureSymbols = store.getState().globalData.configV2.futuresSymbol;
        this._currentSymbol = {} as FutureSymbol;
        this._subscriptions = {};
        this._historyCallbacks = {};
        this._klineHistoryUpdated = false;
        this._indexKlineHistoryUpdated = false;
        this._resolution =
            store.getState().globalData.derivativeSettings?.chartInterval ||
            HEADER_SETTINGS_DEFAULTS.chartInterval;
    }

    onReady = (callback: OnReadyCallback) => {
        setTimeout(() => {
            callback(this.configurationData);
        });
    };

    resolveSymbol = async (
        symbolName: string,
        onSymbolResolvedCallback: (info: LibrarySymbolInfo) => void,
        onResolveErrorCallback: (error: string) => void,
        extension: object,
    ) => {
        const symbolItem = this.getSymbol(symbolName);
        if (!symbolItem) {
            return;
        }

        this._currentSymbol = symbolItem;
        const symbolInfo: LibrarySymbolInfo = {
            ticker: symbolItem.symbolName,
            name: symbolItem.symbolName,
            session: '24x7',
            type: symbolItem.exchange ? 'crypto' : 'index',
            timezone: 'Etc/UTC',
            minmov: 1,
            minmove2: 0,
            pricescale: KLINE_PRICE_ACCURACY[DEPTH[symbolItem.minPricePrecision]],
            has_intraday: true,
            has_weekly_and_monthly: true,
            supported_resolutions: this.configurationData.supported_resolutions,
            volume_precision: DEPTH[symbolItem.basePrecision],
            has_daily: true,
            full_name: symbolItem.symbolName,
            description: '',
            exchange: symbolItem.exchange || '',
            listed_exchange: '',
            format: 'price',
        };
        setTimeout(() => {
            onSymbolResolvedCallback(symbolInfo);
        });
    };

    searchSymbols = (
        searchString: string,
        exchange: string,
        symbolType: string,
        onResult: SearchSymbolsCallback,
    ): void => {
        let symbolNeedsToUpdate: SearchSymbolResultItem[] = [] as SearchSymbolResultItem[];
        let isValidSearch: RegExpMatchArray | null = null;
        if (isValidRegex(searchString)) {
            isValidSearch = searchString.match(new RegExp('^[a-zA-Z-]*$'));
        }
        Object.values(this._futureSymbols).map((symbol) => {
            let matchArr =
                isValidSearch !== null
                    ? symbol.symbolName.match(new RegExp(searchString, 'ig'))
                    : null;
            if (matchArr !== null && matchArr.length > 0 && isValidSearch !== null) {
                symbolNeedsToUpdate.push({
                    ...symbol,
                    full_name: `INDEX_${symbol.symbolName}`,
                    symbol: symbol.symbolId,
                    description: '',
                    type: 'stock',
                    exchange: symbol.exchange || '',
                });
            }
        });
        onResult(symbolNeedsToUpdate);
    };

    getSymbol = (symbolName: string): FutureSymbol | undefined => {
        let localSymbolName = symbolName;
        if (localSymbolName?.startsWith('INDEX_')) {
            localSymbolName = localSymbolName.slice(6);
            let symbolObj = Object.values(this._futureSymbols).find(
                (symbol) => symbol.symbolName === localSymbolName,
            );
            if (symbolObj) {
                symbolObj = { ...symbolObj, symbolName };
            }
            return symbolObj;
        } else {
            let symbolObj = Object.values(this._futureSymbols).find(
                (symbol) => symbol.symbolName === symbolName,
            );
            if (symbolObj) {
                symbolObj = { ...symbolObj, exchange: 'Coinflare' };
            }
            return symbolObj;
        }
    };

    public async getBars(
        symbolInfo: LibrarySymbolInfo,
        resolution: ResolutionString,
        periodParams: PeriodParams,
        onHistoryCallback: HistoryCallback,
        onErrorCallback: ErrorCallback,
    ) {
        this._resolution = resolution;
        const type = symbolInfo.name.startsWith('INDEX_') ? 'INDEX' : 'PRICE';
        const { firstDataRequest, from, to } = periodParams;
        const symbolItem = this.getSymbol(symbolInfo.name);
        if (!symbolItem) {
            return;
        }
        this._currentSymbol = symbolItem;
        this._historyCallbacks[symbolInfo.name] = onHistoryCallback;
        if (firstDataRequest) {
            return this.getHistoryFromStore(symbolInfo.name, from * 1000, to * 1000, type);
        }
        return this.getHistoryFromServer(symbolInfo.name, from * 1000, to * 1000, type);
    }

    async getHistoryFromStore(symbolName: string, from: number, to: number, type: string) {
        const callback = this._historyCallbacks[symbolName];
        if (type === 'INDEX') {
            const indexKlineHistorySub = this._store.subscribe(() => {
                const {
                    indexKlineHistory: kline,
                    indexKlineHistoryUpdated: isUpdated,
                    indexKlineResolution: sResolution,
                } = this._store.getState().socket;

                if (isUpdated && sResolution === getResolution(this._resolution)) {
                    if (
                        kline[0]?.sn ==
                        extractSymbolName(symbolName, DATAFEED_SYMBOL_TYPE.PERPETUAL)
                    ) {
                        const bars = this.getBarsParsedData(kline);
                        this._indexKlineHistoryUpdated = true;
                        callback(bars, {
                            noData: bars.length === 0,
                        });
                        this._store.dispatch(clearKlineData(type) as any);
                        indexKlineHistorySub();
                    } else if (kline.length === 0) {
                        callback([], {
                            noData: true,
                        });
                        indexKlineHistorySub();
                    }
                }
            });
            let timeoutWait = setTimeout(() => {
                if (!this._indexKlineHistoryUpdated) {
                    indexKlineHistorySub();
                    const {
                        indexKlineHistory: kline,
                        indexKlineHistoryUpdated: isUpdated,
                        indexKlineResolution: sResolution,
                    } = this._store.getState().socket;
                    if (isUpdated && sResolution === getResolution(this._resolution)) {
                        if (
                            kline[0]?.sn ==
                            extractSymbolName(symbolName, DATAFEED_SYMBOL_TYPE.PERPETUAL)
                        ) {
                            const bars = this.getBarsParsedData(kline);
                            this._indexKlineHistoryUpdated = true;
                            callback(bars, {
                                noData: bars.length === 0,
                            });
                            this._store.dispatch(clearKlineData(type) as any);
                        } else if (kline.length === 0) {
                            callback([], {
                                noData: true,
                            });
                        }
                    } else {
                        return this.getHistoryFromServer(
                            symbolName,
                            from * 1000,
                            to * 1000,
                            'INDEX',
                        );
                    }
                }
            }, 500);

            if (this._indexKlineHistoryUpdated) {
                this._indexKlineHistoryUpdated = false;
                clearTimeout(timeoutWait);
            }
        } else {
            const klineHistorySub = this._store.subscribe(() => {
                const {
                    klineHistory: kline,
                    klineHistoryUpdated: isUpdated,
                    klineResolution: sResolution,
                } = this._store.getState().socket;

                if (isUpdated && sResolution === getResolution(this._resolution)) {
                    if (
                        extractSymbolName(kline[0]?.sn || 'DEFAULT', DATAFEED_SYMBOL_TYPE.SWAP) ==
                        extractSymbolName(symbolName, DATAFEED_SYMBOL_TYPE.PERPETUAL)
                    ) {
                        const bars = this.getBarsParsedData(kline);
                        this._klineHistoryUpdated = true;
                        callback(bars, {
                            noData: bars.length === 0,
                        });
                        this._store.dispatch(clearKlineData(type) as any);
                        klineHistorySub();
                    } else if (kline.length === 0) {
                        callback([], {
                            noData: true,
                        });
                        klineHistorySub();
                    }
                }
            });
            let timeoutWait = setTimeout(() => {
                if (!this._klineHistoryUpdated) {
                    klineHistorySub();
                    const {
                        klineHistory: kline,
                        klineHistoryUpdated: isUpdated,
                        klineResolution: sResolution,
                    } = this._store.getState().socket;

                    if (isUpdated && sResolution === getResolution(this._resolution)) {
                        if (
                            extractSymbolName(
                                kline[0]?.sn || 'DEFAULT',
                                DATAFEED_SYMBOL_TYPE.SWAP,
                            ) == extractSymbolName(symbolName, DATAFEED_SYMBOL_TYPE.PERPETUAL)
                        ) {
                            const bars = this.getBarsParsedData(kline);
                            this._klineHistoryUpdated = true;
                            callback(bars, {
                                noData: bars.length === 0,
                            });
                            this._store.dispatch(clearKlineData(type) as any);
                        } else if (kline.length === 0) {
                            callback([], {
                                noData: true,
                            });
                        }
                    } else {
                        return this.getHistoryFromServer(
                            symbolName,
                            from * 1000,
                            to * 1000,
                            'PRICE',
                        );
                    }
                }
            }, 500);
            if (this._klineHistoryUpdated) {
                this._klineHistoryUpdated = false;
                clearTimeout(timeoutWait);
            }
        }
    }

    async getHistoryFromServer(symbolName: string, from: number, to: number, type: string) {
        const callback = this._historyCallbacks[symbolName];
        // 2010/01/01 00:00:00
        if (to && to < 1262275200000) {
            callback([], {
                noData: true,
            });
            this._store.dispatch(clearKlineData(type) as any);
            return;
        }
        let limit =
            from && to
                ? Math.min(
                      Math.ceil(
                          (to - from) / (getHoursFromResolution(this._resolution) * 60 * 1000),
                      ),
                      KLINE_MAX_LIMIT,
                  )
                : '';
        try {
            let data: TradeSocketResponseData[] = [];
            const params = new URLSearchParams({
                interval: getResolution(this._resolution),
                from: from ? Math.max(from, 0).toString() : '',
                to: to ? Math.max(to, 0).toString() : '',
                limit: limit ? limit.toString() : '',
            });
            if (type === 'INDEX') {
                params.set('symbol', this._currentSymbol.baseTokenName.replace('-SWAP-', ''));
                const response = await indexKlineHistory(params.toString());
                data = response.data.data;
                const bars = this.getBarsParsedData(data);
                callback(bars, {
                    noData: bars.length === 0,
                });
            } else {
                params.set(
                    'symbol',
                    `${this._currentSymbol.exchangeId}.${this._currentSymbol.symbolId}`,
                );
                const response = await klineHistory(params.toString());
                data = response.data.data;
                const bars = this.getBarsParsedData(data);
                callback(bars, {
                    noData: bars.length === 0,
                });
            }
            this._store.dispatch(clearKlineData(type) as any);
        } catch (err) {
            callback([], {
                noData: true,
            });
            this._store.dispatch(clearKlineData(type) as any);
        }
    }

    private getBarsParsedData(chartData: TradeSocketResponseData[]) {
        const precession = DEPTH[this._currentSymbol.minPricePrecision];
        return chartData.map((data) => ({
            time: data.t,
            open: digits(Number(data.o), precession),
            high: digits(Number(data.h), precession),
            low: digits(Number(data.l), precession),
            close: digits(Number(data.c), precession),
            volume: digits(Number(data.v), precession),
        }));
    }

    private getParsedBar(data: TradeSocketResponseData) {
        const precession = DEPTH[this._currentSymbol.minPricePrecision];
        return {
            time: data.t,
            open: digits(Number(data.o), precession),
            high: digits(Number(data.h), precession),
            low: digits(Number(data.l), precession),
            close: digits(Number(data.c), precession),
            volume: digits(Number(data.v), precession),
        };
    }

    public subscribeBars(
        symbolInfo: LibrarySymbolInfo,
        resolution: string,
        callback: Function,
        listenerGuid: string,
        onResetCacheNeededCallback: () => void,
    ) {
        let subscription;
        this._subscriptions[listenerGuid] = {
            callback,
            symbolInfo,
            resolution,
            lastBarTime: undefined,
        };

        if (symbolInfo.name.startsWith('INDEX_')) {
            subscription = this._store.subscribe(() => {
                const { indexKline: kline, indexKlineResolution: sResolution } =
                    this._store.getState().socket;
                const subscription = this._subscriptions[listenerGuid];
                const candleResolution = getResolution(sResolution || this._resolution);
                if (
                    !isEmptyObj(kline) &&
                    resolution === subscription.resolution &&
                    resolution === this._resolution &&
                    candleResolution === getResolution(this._resolution)
                ) {
                    if (subscription.lastBarTime && kline.t < subscription.lastBarTime) {
                        return;
                    }
                    subscription.callback(this.getParsedBar(kline));
                    this._subscriptions[listenerGuid].lastBarTime = kline.t;
                }
            });
        } else {
            subscription = this._store.subscribe(() => {
                const { kline, klineResolution: sResolution } = this._store.getState().socket;
                const subscription = this._subscriptions[listenerGuid];
                const candleResolution = getResolution(sResolution || this._resolution);
                if (
                    !isEmptyObj(kline) &&
                    resolution === subscription.resolution &&
                    resolution === this._resolution &&
                    candleResolution === getResolution(this._resolution)
                ) {
                    if (subscription.lastBarTime && kline.t < subscription.lastBarTime) {
                        return;
                    }
                    subscription.callback(this.getParsedBar(kline));
                    this._subscriptions[listenerGuid].lastBarTime = kline.t;
                }
            });
        }

        this._subscriptions[listenerGuid].subscription = subscription;
    }

    public unsubscribeBars(listenerGuid: string) {
        const activeKlineSub = this._subscriptions[listenerGuid];
        const activeIndexKlineSub = this._subscriptions[`INDEX_${listenerGuid}`];
        if (activeKlineSub && activeKlineSub.subscription) {
            activeKlineSub.subscription();
            delete this._subscriptions[listenerGuid];
        }
        if (activeIndexKlineSub && activeIndexKlineSub.subscription) {
            activeIndexKlineSub.subscription();
            delete this._subscriptions[`INDEX_${listenerGuid}`];
        }
    }

    public getServerTime(callback: ServerTimeCallback) {
        getServerTime().then((response) => {
            const serverTimeInSeconds = moment(response.data.data.time).unix();
            callback(serverTimeInSeconds);
        });
    }
}

export default DataFeed;
